import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ExaminationsPaneComponent } from '@modules/examination/components/examinations-pane/examinations-pane.component';
import { ExaminationsService } from '@modules/examination/services/examinations.service';
import { PatientActionsService } from '@modules/patient/patient-actions.service';
import { PatientAvatarComponent } from '@modules/patient/patient-avatar/patient-avatar.component';
import { PatientDataComponent } from '@modules/patient/patient-data/patient-data.component';
import { PatientLinkQuickActionComponent } from '@modules/patient/patient-link-quick-action/patient-link-quick-action.component';
import { PatientNamePipe } from '@modules/patient/patient-name.pipe';
import { ActionButtonsComponent } from '@modules/shared/action-buttons/action-buttons.component';
import { ArchivedBadgeComponent } from '@modules/shared/archived-badge/archived-badge.component';
import { ArchivedNotificationComponent } from '@modules/shared/archived-notification/archived-notification.component';
import { BackNavigationButtonComponent } from '@modules/shared/back-navigation-button/back-navigation-button.component';
import { ScrollHeaderTitleDirective } from '@modules/shared/directives/scroll-header-title.directive';
import { ShowIfClientIsGamEnabledDirective } from '@modules/shared/directives/show-if-client-is-gam-enabled.directive';
import { LoaderComponent } from '@modules/shared/loader/loader.component';
import { LottieLabelComponent } from '@modules/shared/lottie-label/lottie-label.component';
import { Store } from '@ngrx/store';
import { IsMobileService } from '@services/is-mobile.service';
import { MenuEntry } from '@shared/models/base/menu-entry';
import { PatientTab } from '@shared/models/enums/patient-tabs.enum';
import { Size } from '@shared/models/enums/size.enum';
import { ActionButton } from '@shared/models/menu-actions/action-buttons';
import { MenuAction } from '@shared/models/menu-actions/menu-action';
import { MenuActionId } from '@shared/models/menu-actions/menu-action-id.enum';
import { JoinPipe } from '@shared/pipes/join.pipe';
import { LastActivityPipe } from '@shared/pipes/last-activity.pipe';
import { selectDepartmentName } from '@shared/store/departments/departments.selectors';
import { HEADER_BREADCRUMB_SEPARATOR } from '@shared/utils/header-utils';
import { injectTabSlideDirection } from '@shared/utils/tab-slide-direction';
import { TuiSlides } from '@taiga-ui/layout';
import { TuiButton } from '@taiga-ui/core';
import { TuiTabs } from '@taiga-ui/kit';
import { TuiNavigation } from '@taiga-ui/layout';
import { combineLatest, distinctUntilChanged, map, Observable, switchMap } from 'rxjs';
import { PatientService } from './patient.service';

@Component({
  templateUrl: './patient.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TuiSlides,
    CommonModule,
    PatientAvatarComponent,
    PatientDataComponent,
    PatientLinkQuickActionComponent,
    ArchivedNotificationComponent,
    ArchivedBadgeComponent,
    ExaminationsPaneComponent,
    LoaderComponent,
    TuiTabs,
    RouterModule,
    ActionButtonsComponent,
    ScrollHeaderTitleDirective,
    TuiNavigation,
    TranslocoDirective,
    LastActivityPipe,
    LottieLabelComponent,
    TuiButton,
    BackNavigationButtonComponent,
    ShowIfClientIsGamEnabledDirective,
  ],
  providers: [PatientService, ExaminationsService],
})
export class PatientComponent implements OnInit {
  private readonly translocoService = inject(TranslocoService);
  private readonly patientActionsService = inject(PatientActionsService);
  private readonly patientPageService = inject(PatientService);
  private readonly store = inject(Store);
  private readonly patientNamePipe = inject(PatientNamePipe);
  private readonly joinPipe = inject(JoinPipe);
  private readonly examinationsService = inject(ExaminationsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly Size = Size;

  readonly patient$ = this.patientPageService.getPatient();
  readonly isPatientStateLoaded$ = this.patientPageService.getIsPatientStateLoaded();

  readonly actionButtonsGroups: ActionButton[] = [
    {
      appearance: 'secondary',
      title: this.translocoService.translate('Rapport'),
      testId: 'patient-report-button',
      actionIds: [MenuActionId.PATIENT_REPORT],
    },
    {
      appearance: 'outline',
      title: this.translocoService.translate('Télésurveiller'),
      testId: 'patient-telemonitor-button',
      actionIds: [MenuActionId.PATIENT_TELEMONITOR],
    },
  ];

  readonly tabs: MenuEntry[] = [
    {
      title: this.translocoService.translate('Galerie'),
      link: PatientTab.SEQUENCES,
      queryParams: {
        is_archived: undefined,
        sequenceId: undefined,
      },
    },
    {
      title: this.translocoService.translate('Lésions'),
      link: PatientTab.ANALYSES,
      queryParams: {
        is_archived: 'false',
        sequenceId: undefined,
      },
    },
    {
      title: this.translocoService.translate('Télésuivis'),
      link: PatientTab.TELEMONITORINGS,
      queryParams: {
        is_archived: 'false',
        sequenceId: undefined,
      },
    },
    {
      title: this.translocoService.translate('Fichiers'),
      link: PatientTab.ATTACHMENTS,
      queryParams: {
        is_archived: undefined,
        sequenceId: undefined,
      },
    },
  ];

  readonly slideDirection = injectTabSlideDirection(this.tabs.map(({ link }) => link));

  readonly patientsBackUrl$ = this.patientPageService
    .getParams()
    .pipe(
      map(({ clientCode, departmentId }) =>
        departmentId ? `/client/${clientCode}/department/${departmentId}/patients` : `/client/${clientCode}/patients`,
      ),
    );
  readonly departmentId$ = this.patientPageService.getParams().pipe(map(({ departmentId }) => departmentId));
  readonly departmentName$ = this.patientPageService
    .getParams()
    .pipe(
      switchMap(({ clientCode, departmentId }) => this.store.select(selectDepartmentName(clientCode, departmentId))),
    );

  private readonly BREAKPOINT_2XL = 1536;
  // Source : https://tailwindcss.com/docs/responsive-design

  readonly isMobile$ = inject(IsMobileService).maxWidth(`${this.BREAKPOINT_2XL - 1}px`);

  get patientActions(): MenuAction[] {
    return this.patientActionsService.getActions();
  }

  get scrollHeaderTitle$(): Observable<string> {
    return combineLatest([this.departmentId$, this.departmentName$, this.patient$]).pipe(
      map(([departmentId, departmentName, patient]) => {
        let titleParts = [this.translocoService.translate('Mes patients')];
        if (departmentId) {
          titleParts = [departmentName, this.translocoService.translate('Patients du groupe')];
        }
        return this.joinPipe.transform(
          [...titleParts, this.patientNamePipe.transform(patient)],
          HEADER_BREADCRUMB_SEPARATOR,
        );
      }),
    );
  }

  ngOnInit(): void {
    this.patientPageService.resetSequences();

    this.patientPageService
      .getParams()
      .pipe(
        distinctUntilChanged(
          (previous, current) =>
            previous.clientCode === current.clientCode &&
            previous.departmentId === current.departmentId &&
            previous.patientId === current.patientId,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ clientCode, departmentId, patientId }) => {
        this.examinationsService.init({ clientCode, departmentId, patientId });
      });
  }
}
